import { Box, CircularProgress, Fade, IconButton, useMediaQuery } from '@mui/material';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import { isEmpty, isUndefined } from 'lodash-es';
import React, { MutableRefObject } from 'react';

import { OpenGovIconMenuItem } from '../Components/icons/OpenGovIconMenuItem';

import { PopperSubmenuContainer } from '../Components/PopperSubmenuContainer';
import { NAV_BAR_SMALL_PHONE_BREAKPOINT, NAV_COLOR_PALETTE } from '../Components/styleConstants';
import { delayedMobileCloseClickHandler } from '../Components/utils';
import { useContextSwitcherDialogController } from '../ContextState/useContextSwitcherDialogController';
import { useMenuContext } from '../ContextState/useMenuContext';
import { AppSwitcherSelectionDialog } from '../ContextSwitcher/AppSwitcher/AppSwitcherSelectionDialog';
// tslint:disable-next-line: max-line-length
import { EntitySwitcherDialog } from '../ContextSwitcher/EntitySwitcher/EntitySwitcherDialog';
import { DATA_TEST } from '../enums';
import {
  BadgeVariant,
  IContextSwitcherData,
  IEntityDetails,
  IFavoritesSection,
  IMenuOption,
  ISwitchMenuOptions,
  UtilityOptions
} from '../types';
import { OfflineIndicator } from '../UtilityTray/Components/OfflineIndicator';
import { UserProfileButton, UserProfileDiv } from '../UtilityTray/Components/ProfileSettingsOption';
import { useUtilityTrayContext } from '../UtilityTray/Context/UtilityTrayContext';
import { CreateNewActionButtonGroup } from '../UtilityTray/CreateNewAction/CreateNewActionButtonGroup';
import { MobileUtilityTray } from '../UtilityTray/Mobile/MobileUtilityTray';
import { SearchActionButton } from '../UtilityTray/SearchAction/SearchActionButton';
import { useEnhancedEffect } from '../utils/utils';

import { focusElementOnEscapeKeydown, focusElementOnShiftTabKeydown } from '../utils/keyboardNavUtils';
import { HamburgerMenuIconButton } from './Components/HamburgerMenuIconButton';
import { MobileKeyboardNavContextProps, useMobileKeyboardNavContext } from './Context/MobileKeyboardNavContext';
import { MobileMenu } from './MobileMenu';

export interface IMobileNavBarProps {
  appName: string;
  menuOptions: IMenuOption[];
  favorites: IFavoritesSection[];
  switchMenuOptions?: ISwitchMenuOptions;
  contextSwitcherData?: IContextSwitcherData;
  showOfflineIndicator?: boolean;
}

// Helper function to calculate the total number of notifications
export const calculateBadgeContent = (
  settingsOptions?: Partial<UtilityOptions>,
  notificationsOptions?: Partial<UtilityOptions>,
  helpOptions?: Partial<UtilityOptions>,
  reportsOptions?: Partial<UtilityOptions>,
  customUtilityOptions?: Partial<UtilityOptions>
): number => {
  return (
    (settingsOptions?.badgeContent || 0) +
    (notificationsOptions?.badgeContent || 0) +
    (helpOptions?.badgeContent || 0) +
    (reportsOptions?.badgeContent || 0) +
    (customUtilityOptions?.badgeContent || 0)
  );
};

// Helper function to check for a badgeVariant key
export const getBadgeVariant = (
  settingsOptions?: Partial<UtilityOptions>,
  notificationsOptions?: Partial<UtilityOptions>,
  helpOptions?: Partial<UtilityOptions>,
  reportsOptions?: Partial<UtilityOptions>,
  customUtilityOptions?: Partial<UtilityOptions>
): BadgeVariant | undefined => {
  const options = [settingsOptions, notificationsOptions, helpOptions, reportsOptions, customUtilityOptions];

  if (options.every((option) => option?.badgeVariant === undefined)) {
    return undefined;
  }

  if (options.some((option) => option?.badgeVariant === 'standard')) {
    return 'standard';
  }

  return 'dot';
};

// Helper function to check if a value is empty or undefined
const isUndefinedOrEmpty = (value) => isUndefined(value) || isEmpty(value);

// Function to determine which options are being used
export const determineUsedItems = (
  menuOptions: IMenuOption[],
  favorites: IFavoritesSection[],
  contextSwitcherData?: IContextSwitcherData,
  switchMenuOptions?: ISwitchMenuOptions,
  disableFavorites?: boolean
) => {
  const isContextSwitcherDataUsed = !isUndefinedOrEmpty(contextSwitcherData);
  const isSwitchMenuOptionsUsed = !isUndefinedOrEmpty(switchMenuOptions);
  const isMenuOptionsUsed = !isUndefinedOrEmpty(menuOptions);
  const isFavoritesUsed = !isUndefinedOrEmpty(favorites) && !disableFavorites;

  return {
    isContextSwitcherDataUsed,
    isSwitchMenuOptionsUsed,
    isMenuOptionsUsed,
    isFavoritesUsed
  };
};

// Helper function to calculate whether to hide hamburger menu
const calculateShouldHideHamburgerMenu = (usedOptions) => {
  // The hamburger menu should be hidden if none of the options are being used
  return Object.values(usedOptions).every((isUsed) => !isUsed);
};

// If an adopting Suite has NOT chosen to include Navigation Menu Items, Favorites, or the Context Switcher
// but has chosen to leverage the Switch Menu Item, the minimum height of the menu does NOT need to be set.
export const determineSubmenuMinHeight = (usedOptions) => {
  const { isContextSwitcherDataUsed, isSwitchMenuOptionsUsed, isMenuOptionsUsed, isFavoritesUsed } = usedOptions;
  return isSwitchMenuOptionsUsed && !isContextSwitcherDataUsed && !isMenuOptionsUsed && !isFavoritesUsed
    ? '0px'
    : '70vh';
};

export const MobileNavBar: React.FC<IMobileNavBarProps> = ({
  appName,
  contextSwitcherData,
  showOfflineIndicator,
  menuOptions,
  switchMenuOptions,
  favorites
}) => {
  const navBarRef = React.useRef<HTMLDivElement>(null);
  const firstTrayMenuItemRef = React.useRef<HTMLLIElement>(null);
  const [utilityTrayOpen, setUtilityTrayOpen] = React.useState(false);
  const hamburgerRef = React.useRef<HTMLButtonElement>(null);
  const profileButtonRef = React.useRef<HTMLButtonElement>(null);
  const [utilityTrayCloseButtonPressed, setUtilityTrayCloseButtonPressed] = React.useState(false);
  const closeUtilityTrayButtonRef = React.useRef<HTMLButtonElement>(null);

  const { disableFavorites, showMainSubmenu, setShowMainSubmenu, setCurrentLevelOneItem } = useMenuContext();
  const { firstMobileMenuItemRef, clearFocusNextStates } =
    useMobileKeyboardNavContext() as MobileKeyboardNavContextProps;

  const closeAndResetMenu = () => {
    if (showMainSubmenu) {
      setCurrentLevelOneItem(null);
      setShowMainSubmenu(false);
      clearFocusNextStates();
    }
  };

  const {
    createNewActionOptions,
    searchOptions,
    profileSettingsOptions,
    settingsOptions,
    notificationsOptions,
    helpOptions,
    reportsOptions,
    customUtilityOptions,
    customProfileSlot
  } = useUtilityTrayContext();

  const hasCustomProfileSlot = !isUndefined(customProfileSlot) && customProfileSlot !== null;

  const badgeContent = calculateBadgeContent(
    settingsOptions,
    notificationsOptions,
    helpOptions,
    reportsOptions,
    customUtilityOptions
  );

  const badgeVariant = getBadgeVariant(
    settingsOptions,
    notificationsOptions,
    helpOptions,
    reportsOptions,
    customUtilityOptions
  );

  const isTabletWidth = useMediaQuery(`(min-width: ${NAV_BAR_SMALL_PHONE_BREAKPOINT}px)`);

  const itemsUsed = determineUsedItems(
    menuOptions,
    favorites,
    contextSwitcherData,
    switchMenuOptions,
    disableFavorites
  );
  const shouldHideHamburgerMenu = calculateShouldHideHamburgerMenu(itemsUsed);
  const submenuMinHeight = determineSubmenuMinHeight(itemsUsed);

  const {
    selectedEntity,
    setSelectedEntity,
    showEntitySelectionDialog,
    setShowEntitySelectionDialog,
    showAppSelectionDialog,
    setShowAppSelectionDialog
  } = useContextSwitcherDialogController();

  // This is a workaround for a potential state where the ref is not yet set,
  // but the popper component needs a reference to the nav bar.
  // You can reproduce the root issue by removing these effects,
  // and then narrowing the nav component with a submenu open.
  // This will cause the mobile submenu to appear, but not be anchored to the nav bar.
  // Passing this fallback ref prevents that behavior, though it is not ideal.
  const [fallbackNavRef, setFallbackNavRef] = React.useState<HTMLElement | null>(null);
  useEnhancedEffect(() => {
    const mobileNavBar = navBarRef?.current;
    setFallbackNavRef(mobileNavBar);
  }, []);

  // This effect is used to focus the profile button when the utility tray close button is pressed.
  // We don't want it to just always move focus when the menu is closed,
  // because otherwise focus would move to it when something else was opened.
  React.useEffect(() => {
    if (utilityTrayCloseButtonPressed && !utilityTrayOpen) {
      profileButtonRef.current?.focus();
      setUtilityTrayCloseButtonPressed(false);
    }
  }, [utilityTrayOpen]);

  const { loadingError } = contextSwitcherData || {};

  // Check if any utility options are present
  const hasUtilityOptions = [
    settingsOptions,
    notificationsOptions,
    helpOptions,
    reportsOptions,
    customUtilityOptions
  ].some((option) => {
    return !isUndefined(option) && !isEmpty(option);
  });

  const hasProfileSettingsOptions = !isUndefined(profileSettingsOptions) && !isEmpty(profileSettingsOptions);

  // The rendered profile settings component is determined by the presence of the profile settings options
  // If only the placeHolderInitials are present, the profile div is rendered
  // If any other options are present, the profile button is rendered
  const { placeHolderInitials, profileImgUrl, ...interactiveSettingsOptions } = profileSettingsOptions || {};

  const hasInteractiveSettingsOptions = Object.values(interactiveSettingsOptions).some(
    (option) => !isUndefined(option)
  );

  // Profile settings submenu needs to appear if any of the options it contains are present
  // This includes the profile settings options, as well as any utility tray options
  const showProfileSettings = hasProfileSettingsOptions || hasUtilityOptions;

  const showProfileDivVariant =
    showProfileSettings &&
    (placeHolderInitials || profileImgUrl) &&
    !hasInteractiveSettingsOptions &&
    !hasUtilityOptions;

  return (
    <>
      <Box
        ref={navBarRef}
        id="mobile-nav-bar"
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        data-test={DATA_TEST.mobile_nav_bar_container}
        component="nav"
        aria-label="Main navigation"
        position="relative"
        flex={1}
        maxHeight="48px"
        boxSizing="border-box"
        p={1.5}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }} role="menu">
          {contextSwitcherData?.loading ? (
            <CircularProgress
              sx={{ mx: '4px', color: NAV_COLOR_PALETTE.primaryBlue }}
              size={12}
              thickness={8}
              data-test={DATA_TEST.context_switcher_loading}
              aria-label="Loading menu"
            />
          ) : (
            <>
              {!shouldHideHamburgerMenu && (
                <Box role="menuitem">
                  <HamburgerMenuIconButton
                    ref={hamburgerRef}
                    menuOpen={showMainSubmenu}
                    handleClick={() => {
                      if (utilityTrayOpen) setUtilityTrayOpen(false);
                      setShowMainSubmenu(true);
                    }}
                    handleKeyDown={(e) => {
                      if (e.key === 'ArrowDown' && firstMobileMenuItemRef?.current) {
                        firstMobileMenuItemRef.current.focus();
                      }
                    }}
                  />
                </Box>
              )}
              <OpenGovIconMenuItem />
              {showOfflineIndicator && <OfflineIndicator condensed={true} />}
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {searchOptions && (
            <SearchActionButton
              onClick={searchOptions.handleSearchIconClick}
              condensed={true}
              showCondensedLabel={isTabletWidth}
            />
          )}
          {createNewActionOptions && (
            <CreateNewActionButtonGroup
              condensed={true}
              showCondensedLabel={isTabletWidth}
              actionItems={createNewActionOptions}
            />
          )}
          {hasCustomProfileSlot && <Box sx={{ display: 'flex', alignItems: 'center' }}>{customProfileSlot}</Box>}
          {!hasCustomProfileSlot && showProfileSettings && (
            <Box sx={{ position: 'relative', padding: 2 }}>
              <Fade in={utilityTrayOpen}>
                <IconButton
                  data-test={DATA_TEST.mobile_utility_options_button}
                  aria-label={utilityTrayOpen ? 'Close the navigation menu' : 'Open the navigation menu'}
                  aria-haspopup="true"
                  aria-expanded={utilityTrayOpen}
                  ref={closeUtilityTrayButtonRef}
                  disableRipple={true}
                  onClick={() => {
                    closeAndResetMenu();
                    setUtilityTrayOpen((o) => !o);
                    setUtilityTrayCloseButtonPressed(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' && firstTrayMenuItemRef?.current) {
                      firstTrayMenuItemRef.current.focus();
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    p: 0,
                    width: 32,
                    height: 32,
                    borderRadius: 0.5,
                    color: NAV_COLOR_PALETTE.primaryBlue
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Fade>

              <Fade in={!utilityTrayOpen}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                  {showProfileDivVariant && (
                    <UserProfileDiv
                      placeHolderInitials={placeHolderInitials || ''}
                      avatarUrl={profileSettingsOptions?.profileImgUrl || ''}
                      size={32}
                    />
                  )}
                  {!showProfileDivVariant && (
                    <UserProfileButton
                      ref={profileButtonRef}
                      collapseOptions={true}
                      badgeContent={badgeContent}
                      badgeVariant={badgeVariant}
                      placeHolderInitials={placeHolderInitials || ''}
                      avatarUrl={profileSettingsOptions?.profileImgUrl || ''}
                      submenuOpen={false}
                      size={32}
                      handleClick={() => {
                        closeAndResetMenu();
                        setUtilityTrayOpen((o) => !o);
                      }}
                    />
                  )}
                </Box>
              </Fade>
            </Box>
          )}
        </Box>

        <PopperSubmenuContainer
          styleOverrides={{ minHeight: { submenuMinHeight }, width: '100vw' }}
          open={showMainSubmenu}
          disablePortal={true}
          placement="bottom-start"
          anchorEl={navBarRef.current || fallbackNavRef}
          dataTest="mobile_menu_popper_container"
          keydownCallbacks={(e, keydownHappenedInContainer) => {
            focusElementOnShiftTabKeydown(e, keydownHappenedInContainer, hamburgerRef?.current);
            focusElementOnEscapeKeydown(e, hamburgerRef?.current);
          }}
          handleClose={() => {
            // Avoids race condition that reopens menu on mobile browsers
            delayedMobileCloseClickHandler(closeAndResetMenu);
          }}
        >
          <MobileMenu
            appName={appName}
            contextSwitcherData={contextSwitcherData}
            handleClose={closeAndResetMenu}
            hamburgerRef={hamburgerRef}
            loadingError={loadingError}
          />
        </PopperSubmenuContainer>

        <PopperSubmenuContainer
          styleOverrides={{ width: '100vw' }}
          open={utilityTrayOpen}
          anchorEl={navBarRef.current}
          placement="bottom-end"
          dataTest="mobile_utility_tray_container"
          keydownCallbacks={(e, keydownHappenedInContainer) => {
            focusElementOnShiftTabKeydown(e, keydownHappenedInContainer, closeUtilityTrayButtonRef?.current);
            if (e.key === 'Escape') {
              setTimeout(() => {
                profileButtonRef.current?.focus();
              }, 200);
            }
          }}
          handleClose={() => {
            // Avoids race condition that reopens menu on mobile browsers
            delayedMobileCloseClickHandler(() => setUtilityTrayOpen(false));
          }}
        >
          <MobileUtilityTray
            firstTrayMenuItemRef={firstTrayMenuItemRef as MutableRefObject<HTMLLIElement>}
            closeUtilityMenu={() => setUtilityTrayOpen(false)}
          />
        </PopperSubmenuContainer>
      </Box>
      {/* Nesting these dialogs inside of a Popper component causes unwanted behavior with
        the entity selection's AutoComplete component.
        Placing them here allows the dialogs to be rendered outside of the Popper component and avoid those issues.
      */}
      {contextSwitcherData && (
        <>
          <EntitySwitcherDialog
            mobile={true}
            open={showEntitySelectionDialog}
            activeEntity={contextSwitcherData.activeEntity}
            selectedEntity={selectedEntity as IEntityDetails}
            entitledEntities={contextSwitcherData.entitledEntities}
            handleEntitySelect={setSelectedEntity}
            handleClose={() => {
              setShowEntitySelectionDialog(false);
            }}
            openAppSelectionDialog={() => setShowAppSelectionDialog(true)}
            forcedDefaultApplication={contextSwitcherData?.forcedDefaultApplication}
            onEntitySelect={contextSwitcherData?.onEntitySelect}
          />
          <AppSwitcherSelectionDialog
            fullScreen={true}
            selectedEntity={selectedEntity as IEntityDetails}
            open={showAppSelectionDialog}
            onEntitySelect={contextSwitcherData?.onEntitySelect}
            handleClose={() => {
              setShowAppSelectionDialog(false);
              setTimeout(() => setSelectedEntity(null), 500);
            }}
          />
        </>
      )}
    </>
  );
};
